import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingMasterData, WarehousingReservationRequest, WarehousingSearchFilters } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-reservations',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    WarehousingPageHeaderComponent,
    WarehousingSearchPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-reservations.component.html',
  styleUrl: './warehousing-reservations.component.css'
})
export class WarehousingReservationsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  isLoading = false;
  requests: WarehousingReservationRequest[] = [];
  warehouses: WarehousingMasterData[] = [];
  conditions: WarehousingMasterData[] = [];
  first = 0;
  rows = 12;

  readonly statusOptions = [
    { label: 'Planned', value: 'PLANNED' },
    { label: 'Partial', value: 'PARTIAL' },
    { label: 'Reserved', value: 'RESERVED' }
  ];

  readonly referenceTypeOptions = [
    { label: 'POSM Project', value: 'POSM_PROJECT_ITEM_SOURCE' }
  ];

  readonly recordOptions = [
    { label: 'Active', value: false },
    { label: 'Deleted', value: true }
  ];

  searchForm = this.fb.group({
    keyword: [''],
    warehouseId: [null as number | null],
    conditionId: [null as number | null],
    status: [null as string | null],
    referenceType: [null as string | null],
    deleted: [false]
  });

  ngOnInit(): void {
    this.restoreListState();
    this.loadReservations();
  }

  get totals(): { requestCount: number; requestedQuantity: number; reservedQuantity: number; remainingQuantity: number } {
    return {
      requestCount: this.requests.length,
      requestedQuantity: this.requests.reduce((sum, request) => sum + (request.requestedQuantity || 0), 0),
      reservedQuantity: this.requests.reduce((sum, request) => sum + (request.reservedQuantity || 0), 0),
      remainingQuantity: this.requests.reduce((sum, request) => sum + (request.remainingQuantity || 0), 0)
    };
  }

  loadReservations(): void {
    this.isLoading = true;
    forkJoin({
      requests: this.warehousingService.getReservationRequests(this.searchFilters()),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.requests = response.requests.data || [];
        this.warehouses = (response.warehouses.data || []).filter(
          row => row.warehouseOwnershipType !== 'THIRD_PARTY_VIRTUAL'
        );
        this.conditions = response.conditions.data || [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Reservations',
          detail: 'Unable to load warehouse reservation requests.'
        });
      }
    });
  }

  applySearch(): void {
    this.first = 0;
    this.persistListState();
    this.loadReservations();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      warehouseId: null,
      conditionId: null,
      status: null,
      referenceType: null,
      deleted: false
    });
    this.first = 0;
    this.rows = 12;
    this.persistListState();
    this.loadReservations();
  }

  onPage(event: { first?: number; rows?: number }): void {
    this.first = Math.max(0, event.first || 0);
    this.rows = [12, 25, 50].includes(event.rows || 12) ? event.rows! : 12;
    this.persistListState();
  }

  reservationListQueryParams(): Record<string, string | number | null> {
    const filters = this.searchFilters();
    return {
      reservationKeyword: filters.keyword?.trim() || null,
      reservationWarehouseId: filters.warehouseId || null,
      reservationConditionId: filters.conditionId || null,
      reservationStatus: filters.status || null,
      reservationReferenceType: filters.referenceType || null,
      reservationDeleted: filters.deleted ? 'true' : null,
      reservationFirst: this.first > 0 ? this.first : null,
      reservationRows: this.rows !== 12 ? this.rows : null
    };
  }

  referenceTypeLabel(referenceType?: string): string {
    return referenceType === 'POSM_PROJECT_ITEM_SOURCE' ? 'POSM Project' : (referenceType || 'Reservation Source');
  }

  reservationSeverity(status?: string): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    const value = `${status || ''}`.toUpperCase();
    if (value === 'RESERVED') return 'success';
    if (value === 'PARTIAL') return 'warn';
    if (value === 'PLANNED') return 'secondary';
    return 'info';
  }

  canViewOrigin(request: WarehousingReservationRequest): boolean {
    return request.referenceType === 'POSM_PROJECT_ITEM_SOURCE' && !!request.referenceHeaderId;
  }

  displayDate(value?: string | Date): string {
    if (!value) return '-';
    return value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  }

  displayDateTime(value?: string | Date): string {
    if (!value) return '-';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).format(date);
  }

  itemSizeLabel(request: WarehousingReservationRequest): string {
    return itemSizeLabel(request);
  }

  reservationTime(request: WarehousingReservationRequest): string | Date | undefined {
    const allocationTimes = (request.allocations || [])
      .map(allocation => allocation.createdAt)
      .filter((value): value is string => !!value)
      .sort((left, right) => this.timestamp(left) - this.timestamp(right));
    return allocationTimes[0] || request.createdAt;
  }

  reservationTimeLabel(request: WarehousingReservationRequest): string {
    return (request.allocations || []).some(allocation => !!allocation.createdAt)
      ? 'First stock allocation'
      : 'Request created';
  }

  allocationCount(request: WarehousingReservationRequest): number {
    return request.allocations?.length || 0;
  }

  private searchFilters(): WarehousingSearchFilters {
    return this.searchForm.getRawValue();
  }

  private restoreListState(): void {
    const query = this.route.snapshot.queryParamMap;
    this.searchForm.patchValue({
      keyword: query.get('reservationKeyword') || '',
      warehouseId: this.queryId(query.get('reservationWarehouseId')),
      conditionId: this.queryId(query.get('reservationConditionId')),
      status: query.get('reservationStatus'),
      referenceType: query.get('reservationReferenceType'),
      deleted: query.get('reservationDeleted') === 'true'
    });
    this.first = this.queryNonNegativeInteger(query.get('reservationFirst')) ?? 0;
    this.rows = [12, 25, 50].includes(this.queryNonNegativeInteger(query.get('reservationRows')) || 12)
      ? (this.queryNonNegativeInteger(query.get('reservationRows')) || 12)
      : 12;
  }

  private persistListState(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.reservationListQueryParams(),
      replaceUrl: true
    });
  }

  private queryId(value: string | null): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private queryNonNegativeInteger(value: string | null): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
  }

  private formatDimension(value?: number): string {
    return value === null || value === undefined
      ? '-'
      : new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);
  }

  private timestamp(value: string | Date): number {
    const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
  }
}
